import { useEffect, useMemo, useState } from "react";
import { ApiService } from "../../../../services/api";
import { MessageSquare, Send, Trash2, ChevronDown, ChevronUp, User as UserIcon, Clock, CornerDownRight } from "lucide-react";
import checkPermission from "../../../../utils/permission";
import ConfirmModal from "../../../../components/modals/ConfirmModal";
import { useModal } from "../../../../hooks/useModal";

type LessonQA = {
  id: string;
  lessonId: string;
  sectionId?: string | null;
  parentId?: string | null;
  userId: string;
  question: string;
  metadata?: any;
  createdAt?: string;
  children?: LessonQA[];
  user?: { firstName?: string; lastName?: string };
};

export default function LessonQAView({
  lessonId,
  sections,
  defaultSectionId,
}: {
  lessonId: string;
  sections: any[];
  defaultSectionId?: string;
}) {
  const { showAlert } = useModal();
  const hasListPermission = checkPermission("lesson_qa_list");
  const hasAddPermission = checkPermission("lesson_qa_add");
  const hasDeletePermission = checkPermission("lesson_qa_delete");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<LessonQA[]>([]);

  const activeSectionId = defaultSectionId || undefined;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [newQuestion, setNewQuestion] = useState("");
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const sectionMap = useMemo(() => {
    const map: Record<string, string> = {};
    (sections || []).forEach((s: any) => (map[s.id] = s.title));
    return map;
  }, [sections]);

  useEffect(() => {
    fetchQAs();
  }, [lessonId, defaultSectionId]);

  const fetchQAs = async () => {
    if (!hasListPermission) return;
    setLoading(true);
    try {
      const res = await ApiService.getLessonQAs(lessonId, {
        limit: 200,
      });
      const allItems: LessonQA[] = Array.isArray(res) ? res : res?.data || [];

      const flat: LessonQA[] = activeSectionId ? allItems.filter((q) => q.sectionId === activeSectionId) : allItems;
      const byId: Record<string, LessonQA> = {};
      const roots: LessonQA[] = [];
      flat.forEach((q) => (byId[q.id] = { ...q, children: [] }));
      flat.forEach((q) => {
        if (q.parentId && byId[q.parentId]) {
          byId[q.parentId].children!.push(byId[q.id]);
        } else {
          roots.push(byId[q.id]);
        }
      });
      setItems(roots);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!hasAddPermission || !newQuestion.trim()) return;
    if (!activeSectionId) {
      await showAlert({
        title: "Uyarı",
        message: "Bir bölüm seçili değil. Lütfen bir içerik/bölüm bağlamında soru oluşturun.",
        type: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      await ApiService.createLessonQA(lessonId, {
        sectionId: activeSectionId,
        question: newQuestion.trim(),
      });
      setNewQuestion("");
      await fetchQAs();
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (parentId: string) => {
    if (!hasAddPermission || !replyText.trim()) return;
    if (!activeSectionId) {
      await showAlert({
        title: "Uyarı",
        message: "Bir bölüm seçili değil. Lütfen bir içerik/bölüm bağlamında cevap yazın.",
        type: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      await ApiService.createLessonQA(lessonId, {
        sectionId: activeSectionId,
        parentId,
        question: replyText.trim(),
      });
      setReplyText("");
      setReplyFor(null);
      await fetchQAs();
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (qaId: string) => {
    if (!hasDeletePermission) return;
    setDeleteId(qaId);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteId(null);
    setDeleteLoading(false);
  };

  const confirmDelete = async () => {
    if (!hasDeletePermission || !deleteId) return;
    setDeleteLoading(true);
    try {
      await ApiService.deleteLessonQA(lessonId, deleteId);
      await fetchQAs();
      closeDelete();
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggle = (id: string, currentOpen: boolean) => setExpanded((p) => ({ ...p, [id]: !currentOpen }));

  const renderNode = (node: LessonQA, depth = 0) => {
    const hasChildren = (node.children || []).length > 0;
    const isOpen = expanded[node.id] !== undefined ? expanded[node.id] : depth === 0;
    const initials = `${(node.user?.firstName || "K").charAt(0)}${(node.user?.lastName || "").charAt(0)}`.toUpperCase();
    const fullName = `${node.user?.firstName || "Kullanıcı"} ${node.user?.lastName || ""}`.trim();
    const created = node.createdAt ? new Date(node.createdAt).toLocaleString("tr-TR") : "";
    const isMainQuestion = depth === 0;

    return (
      <div key={node.id} className="relative group">
        <div
          className={`border border-gray-200 rounded-lg bg-white overflow-hidden hover:bg-gray-50 hover:border-purple-300 transition-all duration-200 ${
            isMainQuestion ? "shadow-sm mb-3" : "mb-2"
          }`}>
          <div className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
              <div className="flex-1 space-y-2 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className="shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center text-[10px] sm:text-[11px] font-semibold text-purple-700">
                      {initials}
                    </div>
                    <span className="text-xs sm:text-sm text-gray-900 font-medium truncate">{fullName}</span>
                  </div>
                  {node.sectionId && (
                    <span className="text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                      {sectionMap[node.sectionId] || "Bölüm"}
                    </span>
                  )}
                  {isMainQuestion ? (
                    <span className="text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                      Soru
                    </span>
                  ) : (
                    <span className="text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-100">
                      Cevap
                    </span>
                  )}
                </div>

                <div className="text-sm sm:text-base text-gray-900 whitespace-pre-wrap leading-relaxed">{node.question}</div>

                {created && (
                  <div className="flex items-center gap-1.5 text-xs sm:text-sm">
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-gray-600">{created}</span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {hasChildren && (
                    <button
                      className="text-[10px] sm:text-xs text-purple-600 hover:text-purple-700 inline-flex items-center gap-1 font-medium"
                      onClick={() => toggle(node.id, isOpen)}>
                      {isOpen ? (
                        <>
                          <ChevronUp className="w-3 h-3" /> Cevapları Gizle
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3" /> {node.children!.length} Cevap
                        </>
                      )}
                    </button>
                  )}
                  {hasAddPermission && (
                    <button
                      className="px-2 sm:px-2.5 py-1 rounded-md text-[10px] sm:text-xs font-medium border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors inline-flex items-center gap-1"
                      onClick={() => setReplyFor(replyFor === node.id ? null : node.id)}>
                      <CornerDownRight className="w-3 h-3" /> Cevap Ver
                    </button>
                  )}
                </div>
              </div>

              {hasDeletePermission && (
                <div className="flex-shrink-0 self-start sm:self-auto">
                  <button
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                    onClick={() => openDelete(node.id)}>
                    <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                    Sil
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {replyFor === node.id && (
          <div className="ml-4 pl-4 pb-3 border-l-2 border-purple-100">
            <div className="bg-gray-50 rounded-lg p-3 mt-2">
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                <textarea
                  className="flex-1 border rounded-lg px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 min-h-[60px]"
                  placeholder="Cevabınızı yazın..."
                  value={replyText}
                  rows={2}
                  onChange={(e) => setReplyText(e.target.value)}
                  disabled={loading}
                />
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm inline-flex items-center gap-1 disabled:opacity-50"
                    onClick={() => handleReply(node.id)}
                    disabled={loading || !replyText.trim()}>
                    <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Gönder
                  </button>
                  <button
                    className="px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg text-xs sm:text-sm"
                    onClick={() => {
                      setReplyText("");
                      setReplyFor(null);
                    }}>
                    İptal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {hasChildren && isOpen && (
          <div className="pl-6 sm:pl-8 pr-1 sm:pr-3 pb-1 sm:pb-3 border-l-2 border-gray-100 ml-4">
            {node.children!.map((c) => renderNode(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!hasListPermission) {
    return <div className="bg-white rounded-lg p-6 border text-center text-gray-600">Bu alanı görüntüleme yetkiniz yok.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-purple-600 flex-shrink-0" />
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex-shrink-0">Soru-Cevap</h2>
        {activeSectionId && (
          <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
            {sectionMap[activeSectionId] || "Bölüm"}
          </span>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm">
        {hasAddPermission && (
          <div>
            <textarea
              className="w-full border rounded-lg px-3 py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-200 min-h-[72px]"
              placeholder={activeSectionId ? `${sectionMap[activeSectionId]} bölümü için soru sorun...` : "Bu bölüm için soru sorun..."}
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              disabled={loading}
              rows={4}
            />
            <div className="mt-2 flex justify-end">
              <button
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50"
                onClick={handleCreate}
                disabled={loading || !newQuestion.trim()}>
                <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Gönder
              </button>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center text-sm sm:text-base text-gray-500">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz Soru Bulunmuyor</h3>
          <p className="text-gray-500">Bu bölüm için ilk sorunuzu sorarak tartışmayı başlatın.</p>
        </div>
      ) : (
        <div className="space-y-3">{items.map((node) => renderNode(node))}</div>
      )}
      <ConfirmModal
        isOpen={deleteOpen}
        onClose={closeDelete}
        onConfirm={confirmDelete}
        title="Soru/Cevabı Sil"
        message="Bu soru/cevabı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        isLoading={deleteLoading}
      />
    </div>
  );
}
